import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import PieChart from 'components/graphs/echarts/pie-chart/PieChart';
import BarChartSimple from 'components/graphs/bar-chart-simple/BarChartSimple';
import Spinner from 'components/spinner/Spinner';
import Sidenote from 'components/sidenote/Sidenote';
import Tooltip from 'components/Tooltip';

import AudienceScore from './audience-score/AudienceScore';
import AudienceDataInterests from './interests/AudienceDataInterests';
import InfluencerCreatorNetwork from '../creator-network/InfluencerCreatorNetwork';

import { cleanInfluencerAudienceData, fetchAudienceData } from '../../reducers/influencerAudienceData';

import { t } from 'utils/language';
import { getCitiesBarChartData, getCountriesBarChartData } from 'utils/charts/bar-chart';
import { colors } from 'utils/charts/utils';
import {
  displayPieChart,
  getAgePieChartData,
  getGenderPieChartData,
  preparePieChartData
} from 'utils/charts/pie-chart';
import { CHANNEL_TYPE_TIKTOK } from 'utils/channels';

import type {
  AudienceData as AudienceDataType,
  InfluencerProfile,
  StatisticsAudienceScore
} from 'types';

type OwnProps = {
  influencerProfile: InfluencerProfile,
  channelUserName: string | undefined | null,
  channelType?: string,
  activeTabMain: string,
  isFreemium: boolean | null | undefined
};

type StateProps = {
  isGatheringData: boolean,
  isLoading: boolean,
  audienceData: AudienceDataType
};

type DispatchProps = {
  dispatchFetchAudienceData: (username: string, channelType?: string) => void,
  dispatchCleanInfluencerAudienceData: () => void
};

type Props = OwnProps & StateProps & DispatchProps;

class AudienceData extends React.PureComponent<Props> {
  componentDidMount() {
    const { channelUserName, channelType, dispatchFetchAudienceData } = this.props;

    if (channelUserName) {
      dispatchFetchAudienceData(channelUserName, channelType);
    }
  }

  componentWillUnmount() {
    const { dispatchCleanInfluencerAudienceData } = this.props;

    dispatchCleanInfluencerAudienceData();
  }

  shouldColumnBeFullWidth(a: boolean, b: boolean, c?: boolean | null): boolean {
    // TODO: I have literally no idea what is written here, we should refactor this someday
    if (c) {
      return a ? !b && !c : b || c;
    }

    return a && !b || b && !a;
  }

  renderCardTitle(key: string): React.ReactNode {
    return (
      <h3 className="influencer-audience__card-title">
        <div className="influencer-audience__card-title-value">
          {t(`influencer_audience_data.section_${key}_title`)}
        </div>
        <div className="influencer-audience__card-title-tooltip">
          <Tooltip
            message={t(`influencer_audience_data.title_${key}_tooltip`)}
            showQuestionMark
          />
        </div>
      </h3>
    );
  }

  renderAge(): React.ReactNode {
    const { audienceData, isLoading } = this.props;

    if (isLoading) {
      return (
        <div className="influencer-audience__chart-loading">
          <Spinner
            position="inline"
            size="small"
            color="light"
          />
        </div>
      );
    }

    const data = getAgePieChartData(audienceData.audienceAgeSplit);
    const pieChartData = preparePieChartData(data, 'audience');

    return (
      <div className="influencer-audience__section influencer-audience__section-chart">
        {this.renderCardTitle('age')}
        <PieChart
          id="audience-age"
          options={pieChartData}
          hide={!displayPieChart(data)}
        />
      </div>
    );
  }

  renderGender(): React.ReactNode {
    const { audienceData, isLoading } = this.props;

    if (isLoading) {
      return (
        <div className="influencer-audience__chart-loading">
          <Spinner
            position="inline"
            size="small"
            color="light"
          />
        </div>
      );
    }

    const data = getGenderPieChartData(audienceData.audienceGenderSplit);
    const pieChartData = preparePieChartData(data, 'audience');

    return (
      <div className="influencer-audience__section influencer-audience__section-chart">
        {this.renderCardTitle('gender')}
        <PieChart
          id="audience-gender"
          options={pieChartData}
          hide={!displayPieChart(data)}
        />
      </div>
    );
  }

  renderCountries(): React.ReactNode {
    const { audienceData, isLoading } = this.props;

    if (isLoading) {
      return (
        <div className="influencer-audience__chart-loading">
          <Spinner
            position="inline"
            size="small"
            color="light"
          />
        </div>
      );
    }

    return (
      <BarChartSimple
        items={getCountriesBarChartData(audienceData.audienceTopCountries, 'percentage')}
        titles={[t('influencer_audience_data.country_title'), '', t('influencer_audience_data.followers'), t('influencer_audience_data.share')]}
        color={colors.audience[1]}
        showPercentMarker
      />
    );
  }

  renderCities(): React.ReactNode {
    const { audienceData, isLoading } = this.props;

    if (isLoading) {
      return (
        <div className="influencer-audience__chart-loading">
          <Spinner
            position="inline"
            size="small"
            color="light"
          />
        </div>
      );
    }

    return (
      <BarChartSimple
        items={getCitiesBarChartData(audienceData.audienceTopCities, 'percentage', false)}
        titles={[t('influencer_audience_data.city_title'), '', t('influencer_audience_data.followers'), t('influencer_audience_data.share')]}
        color={colors.audience[1]}
        showPercentMarker
        hideBarColumn
      />
    );
  }

  renderAudienceScore(
    scoreName: 'credibility' | 'reachability',
    scoreValue?: StatisticsAudienceScore | null
  ): React.ReactNode {
    const { isLoading } = this.props;

    if (isLoading) {
      return (
        <div className="influencer-audience__chart-loading">
          <Spinner
            position="inline"
            size="small"
            color="light"
          />
        </div>
      );
    }

    return (
      <div className="influencer-audience__score">
        <AudienceScore
          scoreName={scoreName}
          scoreValue={scoreValue}
        />
      </div>
    );
  }

  renderInterests(): React.ReactNode {
    // COLLAB-14071 & COLLAB-17556
    if (window.app.env.FEATURE_FACEBOOK_LOGIN) {
      return null;
    }

    const { audienceData, isLoading } = this.props;

    if (isLoading) {
      return (
        <div className="influencer-audience__chart-loading">
          <Spinner
            position="inline"
            size="small"
            color="light"
          />
        </div>
      );
    }

    return (
      <AudienceDataInterests
        interests={audienceData.audienceTopInterests}
      />
    );
  }

  renderContainer(): React.ReactNode {
    const {
      audienceData,
      influencerProfile,
      activeTabMain,
      isFreemium
    } = this.props;
    const showCountries = !isEmpty(audienceData?.audienceTopCountries);
    const showCities = !isEmpty(audienceData?.audienceTopCities);
    const showInterests = !isEmpty(audienceData?.audienceTopInterests);
    const showCredibility = !isNil(audienceData?.audienceCredibility);
    const showReachability = !isEmpty(audienceData?.audienceReachabilityDistribution);

    const isAudienceScoreFullWidth = this.shouldColumnBeFullWidth(showCredibility, showReachability);
    const sectionContainerClasses = classnames('influencer-audience__section-container', {
      'influencer-audience__section-container--full-width': isAudienceScoreFullWidth
    });

    const isChartFullWidth = this.shouldColumnBeFullWidth(showCountries, showCities, showInterests);
    const chartContainerClasses = classnames('influencer-audience__chart', {
      'influencer-audience__chart--full-width': isChartFullWidth
    });

    return (
      <div className="influencer-audience__container">
        <div className="influencer-audience__card">
          <div className="influencer-audience__section-container">
            {this.renderGender()}
            <div className="influencer-audience__section influencer-audience__section-divider" />
            {this.renderAge()}
          </div>
        </div>
        <div className="influencer-audience__card">
          <div className={sectionContainerClasses}>
            {
              showCredibility && (
                <div className="influencer-audience__section">
                  {this.renderCardTitle('audience_credibility')}
                  {this.renderAudienceScore('credibility', audienceData.audienceCredibility)}
                </div>
              )
            }
            {
              showCredibility && showReachability && (
                <div className="influencer-audience__section influencer-audience__section-divider" />
              )
            }
            {
              showReachability && (
                <div className="influencer-audience__section">
                  {this.renderCardTitle('audience_reachability')}
                  {this.renderAudienceScore('reachability', audienceData.audienceReachabilityScore)}
                </div>
              )
            }
          </div>
        </div>
        <div className="influencer-audience__card">
          {
            showCountries && (
              <div className={chartContainerClasses}>
                <div className="influencer-audience__chart-title">
                  {t('influencer_audience_data.section_countries_title')}
                  <span className="influencer-audience__tooltip">
                    <Tooltip
                      message={t('influencer_audience_data.title_countries_tooltip')}
                      showQuestionMark
                    />
                  </span>
                </div>
                {this.renderCountries()}
              </div>
            )
          }
          {
            showCities && (
              <div className={chartContainerClasses}>
                <div className="influencer-audience__chart-title">
                  {t('influencer_audience_data.section_cities_title')}
                  <span className="influencer-audience__tooltip">
                    <Tooltip
                      message={t('influencer_audience_data.title_cities_tooltip')}
                      showQuestionMark
                    />
                  </span>
                </div>
                {this.renderCities()}
              </div>
            )
          }
          {
            showInterests
            // COLLAB-14071 & COLLAB-17556
            && !window.app.env.FEATURE_FACEBOOK_LOGIN
            && (
              <div className={chartContainerClasses}>
                <div className="influencer-audience__chart-title">
                  {t('influencer_audience_data.section_interests_title')}
                  <span className="influencer-audience__tooltip">
                    <Tooltip
                      message={t('influencer_audience_data.title_interests_tooltip')}
                      showQuestionMark
                    />
                  </span>
                </div>
                {this.renderInterests()}
              </div>
            )
          }
        </div>
        {
          activeTabMain !== CHANNEL_TYPE_TIKTOK
          // COLLAB-14071 & COLLAB-17556
          && !window.app.env.FEATURE_FACEBOOK_LOGIN
          && (
            <InfluencerCreatorNetwork
              influencerProfile={influencerProfile}
              isFreemium={isFreemium}
            />
          )
        }
      </div>
    );
  }

  renderGathering(): React.ReactNode {
    return (
      <div className="influencer-audience__gathering">
        <Sidenote
          type="info"
          title={t('progress_gathering_data')}
          message={t('progress_gathering_data_description')}
          className="influencer-audience__gathering-sidenote"
        />
      </div>
    );
  }

  render(): React.ReactNode {
    const { isGatheringData } = this.props;

    return isGatheringData ? this.renderGathering() : this.renderContainer();
  }
}

const mapStateToProps = ({
  influencerAudienceData
}): StateProps => ({
  audienceData: influencerAudienceData.data,
  isLoading: influencerAudienceData.isLoading,
  isGatheringData: influencerAudienceData.isGatheringData
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchFetchAudienceData: (username: string, channelType?: string) =>
    dispatch(fetchAudienceData(username, channelType)),
  dispatchCleanInfluencerAudienceData: () => dispatch(cleanInfluencerAudienceData())
});

export default connect(mapStateToProps, mapDispatchToProps)(AudienceData);
